import { exactCell, type ColumnSpec } from './format';

/**
 * Copy as Markdown and copy as TSV (spec 006 T071, FR-A16).
 *
 * ⚠ **The copy carries the FULL figures, never the compacted faces.** `20.14K` pasted into a
 * spreadsheet is text, not a number — and a reader who copies a table is almost always about to
 * do arithmetic on it. Same divergence the product already makes between a table face and its
 * accessible name.
 */

export interface CopyableResult {
  readonly columns: readonly ColumnSpec[];
  readonly rows: readonly unknown[][];
  readonly totalRows: number;
}

/**
 * Markdown — for Slack and docs.
 *
 * Uses the **full** labels: a pasted table has no hover, so a short code that was legible on
 * screen becomes an unexplained abbreviation in someone else's document.
 */
export function toMarkdown(result: CopyableResult, prose?: string): string {
  const header = `| ${result.columns.map((c) => c.fullLabel).join(' | ')} |`;
  const rule = `| ${result.columns.map(() => '---').join(' | ')} |`;
  const body = result.rows
    .map((row) => `| ${row.map((v, i) => cell(v, result.columns[i])).join(' | ')} |`)
    .join('\n');

  const note =
    result.rows.length < result.totalRows
      ? `\n\n_Showing ${result.rows.length.toLocaleString('en-US')} of ${result.totalRows.toLocaleString('en-US')} rows._`
      : '';

  return [prose?.trim(), [header, rule, body].join('\n') + note].filter(Boolean).join('\n\n');
}

/**
 * TSV — for a paste straight into Sheets or Excel.
 *
 * ⚠ Tabs and newlines inside a value are stripped rather than quoted: TSV has no quoting
 * convention that spreadsheets agree on, and a value containing a tab silently becomes two
 * columns — which shifts every column after it on that row only.
 */
export function toTsv(result: CopyableResult): string {
  const header = result.columns.map((c) => c.fullLabel).join('\t');
  const body = result.rows
    .map((row) => row.map((v, i) => cell(v, result.columns[i]).replace(/[\t\n\r]+/g, ' ')).join('\t'))
    .join('\n');
  return `${header}\n${body}`;
}

function cell(value: unknown, column: ColumnSpec | undefined): string {
  if (!column) return value === null || value === undefined ? '' : String(value);
  return exactCell(value, column.role);
}
